import { useState } from 'react';
import {
    ArrowLeft, Mail, Calendar, MapPin, Phone, Loader2, FileText, ExternalLink
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getInitials = (firstName, lastName) => {
    if (firstName && lastName) {
        return (firstName[0] + lastName[0]).toUpperCase();
    }
    if (firstName) {
        return firstName.substring(0, 2).toUpperCase();
    }
    return '?';
};

export default function RequestDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');

    const { data: offerData, isLoading, error } = useQuery({
        queryKey: ['adminOffer', id],
        queryFn: async () => {
            const response = await api.get(`/company/offers/${id}`);
            return response.data.data;
        },
        enabled: !!id
    });

    const addNote = () => {
        if (newNote.trim()) {
            setNotes([...notes, {
                id: notes.length + 1,
                author: 'You',
                date: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
                content: newNote,
                avatar: 'Y'
            }]);
            setNewNote('');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
        );
    }

    if (error || !offerData) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <p className="text-red-600 mb-4">Failed to load offer details</p>
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
            </div>
        );
    }

    const { quote_owner, service, company, price, message, status, user_agreement, created_at, estimated_days } = offerData;
    const clientName = `${quote_owner?.first_name || ''} ${quote_owner?.last_name || ''}`.trim() || 'Unknown Client';
    const initials = getInitials(quote_owner?.first_name, quote_owner?.last_name);

    return (
        <div className="min-h-screen flex text-sm">
            {/* Main Content - Left */}
            <div className="flex-1 overflow-y-auto">
                {/* Fixed Top Bar */}
                <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
                    <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <h2 className="font-bold text-gray-900">Offer #{id}</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    'bg-gray-100 text-gray-700'
                                }`}>
                                {status}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="px-4 py-5">
                    {/* Client Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
                        <div className="flex items-center justify-between mb-4">
                            <h1 className="text-xl font-bold text-gray-900">{service?.name || 'Service Details'}</h1>
                            <div className="text-xl font-bold text-emerald-600">${price}</div>
                        </div>

                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
                                {initials}
                            </div>
                            <div className="flex-1">
                                <div className="font-bold text-gray-900 text-lg">{clientName}</div>
                                <div className="text-xs text-gray-500 mb-3 underline">Quote Owner</div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-700">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <a href={`mailto:${quote_owner?.email}`} className="text-blue-600 hover:underline">{quote_owner?.email}</a>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-gray-400" />
                                        <span>{quote_owner?.phone || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-8 pt-4 border-t border-gray-200 text-xs">
                            <div>
                                <div className="text-gray-500 mb-0.5">Offered On</div>
                                <div className="font-medium text-gray-900">{formatDate(created_at)}</div>
                            </div>
                            <div>
                                <div className="text-gray-500 mb-0.5">Estimated Duration</div>
                                <div className="font-medium text-gray-900">{estimated_days || 'N/A'} Days</div>
                            </div>
                            <div>
                                <div className="text-gray-500 mb-0.5">Company</div>
                                <div className="font-medium text-gray-900">{company?.name}</div>
                            </div>
                        </div>
                    </div>

                    {/* Offer Message */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
                        <h2 className="text-lg font-bold text-gray-900 mb-4">Offer Message</h2>
                        <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {message || 'No message provided with this offer.'}
                        </div>
                    </div>

                    {/* Documents & Resources */}
                    {(user_agreement) && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Documents</h2>
                            <div className="space-y-3">
                                {user_agreement && (
                                    <a
                                        href={user_agreement}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">User Agreement</div>
                                                <div className="text-xs text-gray-500">Service contract & terms</div>
                                            </div>
                                        </div>
                                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Notes Sidebar */}
            <div className="w-80 border-l border-gray-200 bg-white flex flex-col">
                <div className="p-4 border-b border-gray-200">
                    <h3 className="text-base font-bold text-gray-900">Internal Notes</h3>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {notes.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 italic">No notes added yet</div>
                    ) : (
                        notes.map(note => (
                            <div key={note.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3 text-xs">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                                        {note.avatar}
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">{note.author}</div>
                                        <div className="text-[10px] text-gray-500">{note.date}</div>
                                    </div>
                                </div>
                                <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                            </div>
                        ))
                    )}
                </div>

                {/* Add Note */}
                <div className="p-4 border-t border-gray-200 bg-white">
                    <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), addNote())}
                        placeholder="Add a private note..."
                        className="w-full p-2.5 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500 text-xs text-gray-900"
                        rows="3"
                    />
                    <div className="mt-2 flex justify-end">
                        <button
                            onClick={addNote}
                            disabled={!newNote.trim()}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition"
                        >
                            Save Note
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
